import type {
  ChangeProviderStatusResult,
  ErrorCode,
  ProviderStatus,
  ProviderStatusChangeReason,
} from '@dayon/contracts';

/**
 * Taking a PROVIDER between live, maintenance and disabled.
 *
 * The vocabulary and the sentences, kept out of the screen so the wording is in
 * one place and can be read without the markup around it.
 *
 * `POST /api/games/providers/:id/status` is the only write here. It takes ONE
 * provider — the Games tab's bulk bar has no counterpart on this tab, and a
 * bulk provider form is deliberately a later pass (GM-93).
 */

/**
 * The five reasons a PROVIDER moves, which are the game reasons minus
 * `RTP_DEVIATION`.
 *
 * RTP is measured per title against that title's own declared figure; a
 * provider has no declared RTP to deviate from, so offering the code here would
 * invite an operator to record a measurement that does not exist. The labels
 * are deliberately identical to the game ones: both logs group on the same
 * codes, and two wordings for one code is how a quarterly report grows a
 * translation table.
 */
export const PROVIDER_REASONS = [
  { code: 'SCHEDULED_MAINTENANCE', label: 'Scheduled maintenance window' },
  { code: 'PROVIDER_INCIDENT', label: 'Provider incident or failed integration test' },
  { code: 'COMPLIANCE_HOLD', label: 'Compliance or certification hold' },
  { code: 'COMMERCIAL_DECISION', label: 'Commercial decision' },
  { code: 'CATALOGUE_LAUNCH', label: 'Catalogue launch or relaunch' },
] as const satisfies readonly { code: ProviderStatusChangeReason; label: string }[];

/** The reason most likely to be right for each direction — never the only one. */
export const DEFAULT_PROVIDER_REASON: Record<ProviderStatus, ProviderStatusChangeReason> = {
  LIVE: 'CATALOGUE_LAUNCH',
  MAINTENANCE: 'SCHEDULED_MAINTENANCE',
  DISABLED: 'COMMERCIAL_DECISION',
};

export const PROVIDER_CHANGE_TITLE: Record<ProviderStatus, (name: string) => string> = {
  LIVE: (name) => `Bring ${name} live?`,
  MAINTENANCE: (name) => `Put ${name} into maintenance?`,
  DISABLED: (name) => `Disable ${name}?`,
};

/**
 * What the write actually does, stated before the operator commits.
 *
 * THE ASYMMETRY IS THE POINT. Moving a provider out of LIVE does not touch a
 * single game's own status. The rule a provider's status feeds is enforced on
 * the way UP — `POST /api/games/status` refuses LIVE for a game behind a
 * provider that is not live — and again at launch. Nothing cascades downwards.
 *
 * An operator who takes SA into maintenance and assumes the shelf went dark has
 * been misled by the absence of a sentence, so these say it before the click
 * and the result says it again afterwards with the count.
 */
export const PROVIDER_CHANGE_CONSEQUENCE: Record<ProviderStatus, string> = {
  LIVE:
    'Records the provider as live, with your reason, in the provider log. Games on its shelf keep ' +
    'whatever status they already have — this does not enable any of them. It does remove the ' +
    'block: a game behind a provider that is not live cannot be set live and cannot be opened.',
  MAINTENANCE:
    'Records the provider as in maintenance, with your reason, in the provider log, and blocks ' +
    'every launch on its shelf. It does NOT change any game’s own status: games still marked live ' +
    'stay marked live, and the count below says how many. No round is closed and no player is told.',
  DISABLED:
    'Records the provider as disabled, with your reason, in the provider log, and blocks every ' +
    'launch on its shelf. It does NOT change any game’s own status — the games stay as they are, ' +
    'and the count below says how many are still marked live. Bringing the provider back does not ' +
    'have to touch them either.',
};

/* The note rules moved to ./reason-note.ts when the per-brand write became the
   third route asking for the same thing. Re-exported so the dialog's imports
   did not have to change with them. */
export { NOTE_MAX, NOTE_MIN, noteError, noteIsValid } from './reason-note.ts';

/**
 * WHAT DID NOT HAPPEN, in words.
 *
 * `liveGamesRemaining` is counted inside the same transaction as the move, so
 * it is the state after it rather than a second read that could disagree. The
 * sentence exists because the asymmetry above is invisible on screen: nothing
 * on the Games tab changed, and an operator needs to be told that on purpose
 * rather than discover it.
 */
export function afterCountSentence(result: ChangeProviderStatusResult): string {
  const { name, status, liveGamesRemaining: live, gamesOnShelf: shelf } = result;
  if (shelf === 0) return `${name} has no games on its shelf, so nothing else was affected.`;
  const of = `${live} of ${shelf} game${shelf === 1 ? '' : 's'}`;
  if (status === 'LIVE')
    return `${name} is live. ${of} on its shelf ${live === 1 ? 'is' : 'are'} marked live — bringing a provider back does not enable anything on its own.`;
  if (live === 0)
    return `${name} is ${status === 'MAINTENANCE' ? 'in maintenance' : 'disabled'}, and no game on its shelf is marked live.`;
  return `${name} is ${status === 'MAINTENANCE' ? 'in maintenance' : 'disabled'}, but ${of} on its shelf ${live === 1 ? 'is' : 'are'} still marked live. Launches are blocked while the provider is not live; the games themselves were not changed.`;
}

/**
 * Why a refused change was refused, as a heading.
 *
 * The server's own sentence is always the message — it is more precise than
 * anything restated here. What this picks is the TITLE, and it picks it from
 * what this session knows rather than by reading the prose: `games.manage` and
 * the two-factor rule both come back as 403 with different sentences, and
 * matching on the words would break the first time one is reworded.
 *
 * THERE IS NO `TWO_FACTOR_REQUIRED` BRANCH ON PURPOSE. The code exists, but no
 * back-office route can emit it: `assertCan` throws `TwoFactorRequiredError`
 * and `requirePermission` catches it and re-throws `forbidden(...)`
 * unconditionally, so every second-factor refusal here arrives as FORBIDDEN
 * and is told apart above by what this session knows. The only emitters are in
 * the PLAYER realm's step-up, which this console never calls. A branch for a
 * code that cannot arrive is the defect this function was just fixed for.
 */
export function refusalTitle(
  code: ErrorCode | undefined,
  providerName: string,
  session: { allowed: boolean; mfa: boolean },
): string {
  if (code === 'FORBIDDEN' || code === 'UNAUTHENTICATED') {
    if (!session.allowed) return 'That needs the games.manage permission';
    if (!session.mfa) return 'That needs a two-factor verified session';
    return `${providerName} was not changed`;
  }
  if (code === 'CONFLICT') return `${providerName} was already that status`;
  if (code === 'VALIDATION_FAILED') return 'That reason was not accepted';
  if (code === 'NOT_FOUND') return `${providerName} no longer exists`;
  return `${providerName} was not changed`;
}
